import React, { useMemo, useState } from 'react'
import { Section } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import { CommonButton } from 'components/Common'
import styled from '@emotion/styled'
import { Checkbox, Table } from 'antd'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import type { TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  LightspeedStore,
  getMenuOrderType,
  MENU_ORDER_TYPE_LABELS
} from './types'
import dayjs from 'dayjs'
import DisconnectLocationModal from './DisconnectLocationModal'
import { DeleteButton } from './styles'
import { useStoreIntegration } from './hooks/useStoreIntegration'
import { useTableSelection } from '../useTableSelection'

interface LocationSectionProps {
  stepStatus: StepStatus
  connectedLocations: LightspeedStore[]
  onConnect: () => void
  onSelect: (location: LightspeedStore) => void
  onBulkDisconnect: () => void
  onBulkSyncMenu: () => void
  isLoading: boolean
}

const LocationSection = ({
  connectedLocations,
  stepStatus,
  onConnect,
  onSelect,
  onBulkDisconnect,
  onBulkSyncMenu,
  isLoading
}: LocationSectionProps) => {
  const allIds = useMemo(
    () => connectedLocations.map((l) => l.id),
    [connectedLocations]
  )
  const {
    selectedIds,
    isSelected,
    toggleRow,
    toggleAll,
    isAllSelected: isAllRowsSelected,
    hasSelection: hasSelectedRows
  } = useTableSelection(allIds)

  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false)
  const { disconnectStore, syncMenu } = useStoreIntegration()
  const { addMessage, clearErrors } = useMessageStore()

  const nameById = useMemo(
    () =>
      connectedLocations.reduce<Record<string, string>>((acc, l) => {
        acc[l.id] = l.name
        return acc
      }, {}),
    [connectedLocations]
  )

  const selectedNames = useMemo(
    () => selectedIds.map((id) => nameById[id]).filter((n): n is string => !!n),
    [selectedIds, nameById]
  )

  const handleDisconnectCancel = () => {
    setIsDisconnectModalOpen(false)
  }

  const handleCompleteBulkDisconnect = async () => {
    const promises = selectedIds.map((id) => disconnectStore({ storeId: id }))

    try {
      await Promise.all(promises)
      setIsDisconnectModalOpen(false)

      addMessage({
        type: 'message',
        content: 'Connections deleted successfully!'
      })

      onBulkDisconnect()
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to delete connections!'
      })

      onBulkDisconnect()
    }
  }

  const handleBulkDisconnect = () => {
    if (!hasSelectedRows) {
      return
    }

    setIsDisconnectModalOpen(true)
  }

  const handleBulkSyncMenu = async () => {
    if (!hasSelectedRows) {
      return
    }

    clearErrors()
    const promises = selectedIds.map((id) => syncMenu(id))

    try {
      await Promise.all(promises)

      addMessage({
        type: 'message',
        content: 'Menus synced successfully!'
      })
    } catch (error) {
      addMessage({
        type: 'error',
        content: 'Failed to sync menus: ' + (error as Error).message
      })
    } finally {
      onBulkSyncMenu()
    }
  }

  const handleConnectStore = () => {
    onConnect()
  }

  const COLUMNS: ColumnsType<LightspeedStore> = [
    {
      title: (
        <StyledCheckbox
          value={'all'}
          checked={isAllRowsSelected}
          onChange={toggleAll}
        />
      ),
      dataIndex: 'id',
      key: 'id',
      width: 50,
      render: (id: string) => {
        return (
          <StyledCheckbox
            value={id}
            checked={isSelected(id)}
            onChange={() => toggleRow(id)}
          />
        )
      }
    },
    {
      title: 'Slerp Store',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => {
        return <Cell>{name}</Cell>
      }
    },
    {
      title: 'Lightspeed Location',
      dataIndex: [
        'store_integration_settings',
        'lightspeed_business_location_name'
      ],
      key: 'lightspeed_business_location_name',
      render: (locationName: string) => {
        return <Cell>{locationName}</Cell>
      }
    },
    {
      title: 'Menu',
      dataIndex: ['store_integration_settings', 'lightspeed_menus'],
      key: 'lightspeed_menus',
      render: (
        menus: LightspeedStore['store_integration_settings']['lightspeed_menus']
      ) => {
        return (
          <Cell>
            {menus.map((menu) => {
              const orderType = getMenuOrderType(menu)
              const label = orderType
                ? MENU_ORDER_TYPE_LABELS[orderType]
                : undefined
              return (
                <div key={menu.id}>
                  {label ? `${label}: ${menu.name}` : menu.name}
                </div>
              )
            })}
          </Cell>
        )
      }
    },
    {
      title: 'Last Sync',
      dataIndex: ['store_integration_settings', 'lightspeed_last_menu_push_at'],
      key: 'lightspeed_last_menu_push_at',
      render: (lastSync: string | null) => {
        if (!lastSync) {
          return <Cell>-</Cell>
        }

        return (
          <>
            <Cell>{dayjs(lastSync).format('DD/MM/YYYY')}</Cell>
            <Cell>{dayjs(lastSync).format('HH:mm')}</Cell>
          </>
        )
      }
    }
  ]

  return (
    <Section
      title='Connect Stores and Menus'
      description='Connect each store to a Lightspeed location and assign the menu it should use. Only menus from the connected location are available.'
      stepStatus={stepStatus}
      stepNumber={2}
    >
      {connectedLocations.length > 0 && (
        <div>
          <ActionBanner>
            <Description>
              <Title>Bulk Actions</Title>
              <Subtitle>
                Select one or more rows, then choose a bulk action.
              </Subtitle>
            </Description>

            <Actions inactive={!hasSelectedRows}>
              <DeleteButton onClick={handleBulkDisconnect}>
                Delete Connection(s)
              </DeleteButton>
              <CommonButton onClick={handleBulkSyncMenu}>
                Sync menu(s)
              </CommonButton>
            </Actions>
          </ActionBanner>

          <StyledTable
            data-testid='campaigns-history-table'
            dataSource={connectedLocations}
            columns={COLUMNS as TableProps<object>['columns']}
            pagination={false}
            rowKey='id'
            loading={isLoading}
            onRow={(record) => {
              const typedRecord = record as LightspeedStore
              return {
                onClick: (event: React.MouseEvent) => {
                  // Prevent row click when clicking on checkbox
                  const target = event.target as HTMLElement
                  if (
                    target.closest('.ant-checkbox') ||
                    target.closest('.ant-checkbox-wrapper')
                  ) {
                    return
                  }
                  onSelect(typedRecord)
                }
              }
            }}
          />
        </div>
      )}

      <CommonButton type='ghost' onClick={handleConnectStore}>
        Connect a Store
      </CommonButton>

      <DisconnectLocationModal
        selectedRows={selectedNames}
        isOpen={isDisconnectModalOpen}
        onCancel={handleDisconnectCancel}
        onOk={handleCompleteBulkDisconnect}
      />
    </Section>
  )
}

const StyledCheckbox = styled(Checkbox)`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: -12px;
    width: 48px;
    height: 48px;
    z-index: 1;
  }
`

const Cell = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #4a4a68;
  line-height: 1.2;
`

const StyledTable = styled(Table)`
  .ant-table-tbody > tr {
    cursor: pointer;

    &:hover {
      background-color: #fafafb;
    }
  }
  border: 1px solid #dbd9df;
  border-radius: 6px;
  overflow: hidden;

  .ant-table-row {
    > .ant-table-cell {
      border-bottom: 1px solid #dbd9df;
      padding: 12px;
      line-height: 1;
    }

    &:last-of-type {
      > .ant-table-cell {
        border-bottom: none;
      }
    }
  }

  thead > tr {
    > .ant-table-cell {
      background: #f3f2f4;
      border-bottom: 1px solid #dbd9df;
      padding: 12px;
      line-height: 1;
    }
  }

  .ant-table-cell {
    position: relative;

    &:not(:first-of-type):not(:last-of-type) {
      &::after {
        content: '';
        position: absolute;
        top: 12px;
        bottom: 12px;
        right: 0;
        width: 1px;
        background: #d2d2d9;
      }
    }
  }
`

const ActionBanner = styled.div`
  border: 1px solid #d2d2d9;
  border-radius: 4px;
  padding: 8px 12px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const Actions = styled.div<{ inactive: boolean }>(
  ({ inactive }) => `
  display: flex;
  gap: 24px;

  ${
    inactive &&
    `
    opacity: 0.4;
    pointer-events: none;
  `
  }
`
)

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a68;
  line-height: 1;
`

const Subtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #4a4a68;
  line-height: 1;
`

export default LocationSection
